import React, { useCallback, useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { Crosshair } from "@/components/Crosshair";
import UsChoroplethMap from "@/components/UsChoroplethMap";
import { api } from "@/lib/api";
import { MapPin, Building2, Users, X, ChevronRight, Loader2, ShieldCheck, Lock } from "lucide-react";
import { usePageContent, AccentText } from "@/lib/pageContent";

const LAYER_ICON = { verified_sellers: Users, businesses: Building2, inspection_facilities: MapPin };
const UNSHADED = "#22271D";
const BASE_FILL = "#0D0E0F";
const olive = (i) => `rgba(160,180,90,${i})`;

// Two GENUINELY SEPARATE fill rules. The INDIVIDUALS (verified_sellers) heat map is server-banded
// — the backend hands us the exact band fill per state (privacy + banding decided server-side).
// The BUSINESSES layer keeps its own intensity ramp. They never share a path, so a change to one
// layer's shading can't silently alter the other.
function fillForSellers(datum) {
  return datum?.fill || BASE_FILL;               // zero-count states -> base fill, never unstyled
}
function fillForBusinesses(datum) {
  if (!datum) return UNSHADED;
  if (datum.present) return olive(0.28);
  return olive(datum.intensity || 0.5);
}
function fillFor(datum, layer) {
  return layer === "businesses" ? fillForBusinesses(datum) : fillForSellers(datum);
}

export default function MapPage() {
  const { get } = usePageContent("map");
  const [layers, setLayers] = useState([]);
  const [activeLayer, setActiveLayer] = useState("verified_sellers");
  const [statesData, setStatesData] = useState(null); // {states, note, ...}
  const [selected, setSelected] = useState(null);      // USPS code
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/map/layers").then(({ data }) => setLayers(data.layers || [])).catch(() => {});
  }, []);

  const loadStates = useCallback(async (layer) => {
    setLoading(true); setSelected(null); setDetail(null);
    try {
      const { data } = await api.get("/map/states", { params: { layer } });
      setStatesData(data);
    } catch { setStatesData({ states: {}, note: "" }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStates(activeLayer); }, [activeLayer, loadStates]);

  const selectState = useCallback(async (code) => {
    if (!code) return;
    setSelected(code); setDetail(null);
    try {
      const { data } = await api.get(`/map/state/${code}`, { params: { layer: activeLayer } });
      setDetail(data);
    } catch { setDetail({ summary: "Unavailable.", businesses: [] }); }
  }, [activeLayer]);

  // Escape returns to the national view.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setSelected(null); setDetail(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const states = statesData?.states || {};
  const hasData = ((statesData?.shown_states || 0) + (statesData?.present_states || 0)) > 0;
  const activeMeta = layers.find((l) => l.id === activeLayer);

  const styleFor = (geo, code, datum) => {
    const isSel = code && code === selected;
    const base = fillFor(datum, activeLayer);
    return {
      default: {
        fill: base, stroke: isSel ? "#CCFF00" : "#8DA063",
        strokeWidth: isSel ? 1.6 : 0.7, outline: "none", cursor: "pointer",
      },
      hover: { fill: base, stroke: "#CCFF00", strokeWidth: 1, outline: "none", filter: "brightness(1.4)" },
      pressed: { outline: "none" },
    };
  };

  return (
    <div className="min-h-screen text-foreground" data-testid="map-page">
      <Navbar />
      <div className="max-w-[1100px] mx-auto px-4 py-16 sm:py-20">
        <div className="text-center animate-fade-up">
          <span className="font-mono text-[10px] uppercase tracking-[3.4px] text-volt">{get("eyebrow", "Coverage")}</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] mt-4">
            <AccentText text={get("heading", "REGIONAL\n*COVERAGE MAP.*")} />
          </h1>
          <p className="mt-5 text-base text-secondary-foreground font-body max-w-[58ch] mx-auto">
            {get("subhead", "Verified-seller density and business coverage across the country, by state. Choose a layer, then click a state to see what's there.")}
          </p>
        </div>

        <Crosshair className="my-14" />

        {/* layer toggles (the cards double as the layer switch) */}
        <div className="grid sm:grid-cols-3 gap-3 mb-4" data-testid="map-legend">
          {layers.map((l) => {
            const Icon = LAYER_ICON[l.id] || MapPin;
            const active = l.id === activeLayer && l.enabled;
            return (
              <button
                key={l.id}
                data-testid={`map-layer-${l.id}`}
                disabled={!l.enabled}
                onClick={() => l.enabled && setActiveLayer(l.id)}
                className={`text-left border p-4 transition-colors ${active ? "border-olive-bright bg-olive/15" : "border-sniper bg-surface"} ${l.enabled ? "hover:border-olive cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`w-5 h-5 shrink-0 ${active ? "text-volt" : "text-olive-bright"}`} strokeWidth={1.8} />
                  <h3 className="font-heading text-sm font-bold uppercase tracking-tight">{l.label}</h3>
                  {!l.enabled && (
                    <span className="ml-auto font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-sniper text-muted-foreground" data-testid={`map-layer-soon-${l.id}`}>Coming soon</span>
                  )}
                </div>
                <p className="mt-1.5 text-sm text-secondary-foreground font-body">{l.note}</p>
              </button>
            );
          })}
        </div>

        {/* HEAT-MAP LEGEND (individuals layer only). Bands are read straight from the server so the
            client never derives — or prints — a live maximum. Ranges only, never a raw max. */}
        {activeLayer === "verified_sellers" && (statesData?.bands || []).length > 0 && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3 font-mono text-[10px] uppercase tracking-wide" data-testid="map-heat-legend">
            <span className="text-muted-foreground">Verified sellers / state</span>
            {statesData.bands.map((b) => (
              <span key={b.index} className="inline-flex items-center gap-1.5" data-testid={`map-legend-band-${b.index}`}>
                <span className="inline-block w-4 h-3 border border-sniper shrink-0" style={{ background: b.fill }} />
                <span className="text-secondary-foreground">{b.range}{b.kind === "present" ? " · present" : ""}</span>
              </span>
            ))}
          </div>
        )}

        {/* breadcrumb */}
        <div className="flex items-center gap-1.5 font-mono text-[11px] uppercase mb-2" data-testid="map-breadcrumb">
          <button onClick={() => { setSelected(null); setDetail(null); }} data-testid="map-breadcrumb-national"
            className={selected ? "text-muted-foreground hover:text-foreground" : "text-olive-bright"}>United States</button>
          {selected && <><ChevronRight className="w-3 h-3 text-muted-foreground" /><span className="text-olive-bright" data-testid="map-breadcrumb-state">{selected}</span></>}
          {loading && <Loader2 className="w-3 h-3 animate-spin text-olive-bright ml-1" />}
        </div>

        {/* map */}
        <div className="relative border border-sniper bg-black overflow-hidden" data-testid="map-shell">
          <UsChoroplethMap
            svgTestId="map-choropleth"
            zoomable
            datumForCode={(code) => states[code] || null}
            labelFor={(d) => (d?.shown ? String(d.count) : null)}
            onSelect={(code) => code && selectState(code)}
            stateTestId={(code, name) => `map-state-${code || name}`}
            styleFor={styleFor}
            height={560}
          />

          {/* sparse / empty layer state — no fake pins, just the truth about what will appear */}
          {!loading && !hasData && (
            <div className="absolute inset-x-0 bottom-4 flex justify-center px-6" data-testid={`map-empty-${activeLayer}`}>
              <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground text-center max-w-[60ch] bg-black/70 px-4 py-2 border border-sniper">
                {activeMeta?.empty_copy || "Nothing to show on this layer yet."}
              </p>
            </div>
          )}

          {/* state detail panel */}
          {selected && (
            <div className="absolute top-3 left-3 w-[300px] max-w-[92%] bg-black/95 border border-olive/40 p-4 z-10" data-testid="map-state-panel">
              <div className="flex items-center justify-between mb-2">
                <span className="font-heading text-sm uppercase text-olive-bright">{selected}</span>
                <button onClick={() => { setSelected(null); setDetail(null); }} data-testid="map-state-close"><X className="w-4 h-4 text-muted-foreground hover:text-foreground" /></button>
              </div>
              {!detail ? (
                <div className="flex items-center gap-2 text-muted-foreground font-mono text-xs py-3"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : (
                <div className="font-mono text-xs space-y-2">
                  <p className="text-foreground flex items-start gap-1.5">
                    {detail.suppressed ? <Lock className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" /> : <ShieldCheck className="w-3.5 h-3.5 text-olive-bright shrink-0 mt-0.5" />}
                    <span>{detail.summary}</span>
                  </p>
                  {detail.suppressed && (
                    <p className="text-muted-foreground/80 leading-snug">Fewer than {statesData?.threshold || 5} — shown as present only to protect individual privacy.</p>
                  )}
                  {(detail.businesses || []).length > 0 && (
                    <div className="pt-2 border-t border-sniper" data-testid="map-state-businesses">
                      <p className="text-muted-foreground uppercase text-[10px] mb-1">Listed businesses</p>
                      {detail.businesses.map((b) => (
                        <div key={b.username} className="text-foreground py-0.5">{b.name}{b.city ? <span className="text-muted-foreground"> · {b.city}</span> : null}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* privacy note — a trust feature, said plainly */}
        <p className="mt-3 font-mono text-[11px] text-muted-foreground leading-relaxed" data-testid="map-privacy-note">
          This map shows verified members by state and never individual locations. States with fewer than {statesData?.threshold || 5} verified
          sellers appear as present only — no number, no city, no address. Businesses are public commercial entities and appear only when they opt in.
        </p>
      </div>
    </div>
  );
}
